import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import {
  FieldArrayType,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { SessionStorageService } from '../../../services/session-storage.service';
import { MaskService } from '../../../services/mask.service';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MatDialog } from '@angular/material/dialog';
import { FormService } from '../../form-service.service';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CurrencyPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-formly-tabbed-repeat-section',
  styleUrls: ['./form-tabbedRepeat.component.scss'],
  templateUrl: `./form-tabbedRepeat.component.html`,
  imports: [
    MatTabsModule,
    MatIconModule,
    FormlyModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    CurrencyPipe,
    MatButtonModule,
  ],
})
export class FormlyTabbedRepeatComponent
  extends FieldArrayType
  implements OnInit, AfterViewInit
{
  private dialog = inject(MatDialog);
  private ss = inject(SessionStorageService);
  private detector = inject(ChangeDetectorRef);
  private formSvc = inject(FormService);
  private mask = inject(MaskService);

  @HostBinding('class') classes = 'w-100 form-section';
  @ViewChild(MatTabGroup) tabs?: MatTabGroup;

  needsRowId = false;
  rowIdPrefix: string;
  tspRowIdPrefix: string;
  idVar: string;
  tspIdVar: string;
  parentIdVar: string;
  tspParentIdVar: string;
  disableAdd = false;
  maxEntries = 0;
  minEntries = false;
  mode: string;
  program: any;
  loading = false;
  selectedIndex = 0;
  hideAddRemove = false;

  ngOnInit() {
    this.onPopulate(this.field);
    const globals = this.ss.get('globals');

    this.maxEntries =
      this.key === 'Five29Plans'
        ? globals.GlobalsGroup?.UnitSettings?.MaxSelectedPlans
        : globals.GlobalsGroup?.UnitSettings?.MaxSelectProducts;

    if (
      (this.field.fieldArray['props'].defaultOpen &&
        this.formControl.controls.length === 0) ||
      (this.field.fieldArray['props'].minEntries &&
        this.field.model.length === 0)
    ) {
      this.add();
    }

    const vars = this.field.fieldArray['fieldGroup'].map(x => x.key);
    const parentVars = this.field.parent.fieldGroup.map(x => x.key);
    this.parentIdVar = parentVars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;

    this.tspParentIdVar = parentVars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    this.idVar = vars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;
    this.tspIdVar = vars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    if (this.idVar || this.tspIdVar) {
      this.needsRowId = true;
      this.rowIdPrefix = this.idVar ? this.idVar.split('_')[0] : this.idVar;
      this.tspRowIdPrefix = this.tspIdVar
        ? this.tspIdVar.split('_')[0]
        : this.tspIdVar;
    }

    if (
      this.field.fieldArray['props'].disableCheck ||
      (this.field.fieldArray['props'].entryLimit &&
        this.maxEntries > 0 &&
        this.field.model.length >= this.maxEntries)
    ) {
      this.disableAdd = true;
      this.detector.detectChanges();
    }

    if (globals?.GlobalsGroup?.UnitSettings?.HideAddRemoveSource) {
      this.hideAddRemove = true;
    }
    if (this.needsRowId || this.field.key === 'exchange') {
      this.field.fieldGroup.forEach((group, idx) => {
        group.fieldGroup.forEach(x => {
          if (x.key === 'CurrentAnnuityDetailsArray_IsExchange') {
            group['isExchange'] = x.formControl.value;
          }

          const fieldKey = x.key as string;

          if (fieldKey.includes('SourceBridgeID')) {
            if (!x.formControl.value) {
              x.formControl.setValue(`${this.rowIdPrefix}-${idx}`);
            }
          }

          if (fieldKey.includes('TSPSourceID')) {
            if (!x.formControl.value) {
              x.formControl.setValue(`TSPFundDetails-${idx}`);
            }
          }
        });
      });
    }
  }

  ngAfterViewInit() {
    this.registerTabClick();
  }

  getAddBtnLabel() {
    const foundLabel =
      this.field.parent?.model?.[
        this.field.parent?.props?.label?.split('{{')[1]?.replace('}}', '')
      ];
    if (foundLabel) {
      return `Additional ${foundLabel}`;
    }
    return null;
  }

  registerTabClick(): void {
    // Get the handler reference
    const handleTabClick = this.tabs._handleClick;

    this.tabs._handleClick = (tab, header, index) => {
      // If the click is NOT the add button change tabs, otherwise add new index
      if (index <= this.field.fieldGroup.length - 1) {
        handleTabClick.apply(this.tabs, [tab, header, index]);

        // We update the currentIndex, as we need it again when
        // another tab is clicked
        this.selectedIndex = index;
      } else {
        this.loading = true;
        // Timeout to ensure that the loading animation happens for smoother UX
        setTimeout(() => {
          this.add();
          this.detector.detectChanges();
        }, 300);
      }
    };
  }

  handleKeyUpAdd(ev) {
    if (ev === this.field.fieldGroup.length) {
      this.loading = true;
      setTimeout(() => {
        this.add();
        this.detector.detectChanges();
      });
    }
  }

  toggleControlSwitch(index) {
    const controlField = this.getField(
      this.field,
      'CurrentAnnuityDetailsArray_IsExchange',
      index
    );
    const theControl = controlField.formControl.value;
    controlField.formControl.setValue(!theControl);
    this.field.fieldGroup[index]['isExchange'] =
      !this.field.fieldGroup[index]['isExchange'];
  }

  add(navToTop?) {
    super.add();
    const row = this.model.length > 0 ? this.model.length - 1 : 0;
    if (this.field.key === 'exchange' && this.field.fieldGroup.length > 0) {
      this.field.fieldGroup[row]['isExchange'] = true;
    }

    if (this.needsRowId) {
      let existingRowValues = this.model.map(x => {
        try {
          if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
            return +x[this.tspIdVar].split('_')[1];
          } else {
            return +x[this.idVar].split('_')[1];
          }
        } catch (error) {
          return null;
        }
      });

      existingRowValues = existingRowValues.filter(
        x => x !== null || x !== undefined
      );

      const rowPrefix =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.tspRowIdPrefix
          : this.rowIdPrefix;
      const highestIdx =
        existingRowValues.length > 0 ? Math.max(...existingRowValues) : null;
      let newIdx = 0;

      if (this.parentIdVar || this.tspParentIdVar) {
        newIdx = +this.field.parent.key;
      } else {
        newIdx = !highestIdx || row > highestIdx ? row : highestIdx + 1;
      }

      const parentIdField =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.getField(this.field, this.tspIdVar, row)
          : this.getField(this.field, this.idVar, row);

      if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
        if (!this.field.parent.model[this.tspParentIdVar]) {
          this.field.parent.model[this.tspParentIdVar] =
            `${rowPrefix}-${newIdx}`;
        }
      } else {
        if (!this.field.parent.model[this.parentIdVar]) {
          this.field.parent.model[this.parentIdVar] = `${rowPrefix}-${newIdx}`;
          parentIdField.formControl.setValue(`${rowPrefix}-${newIdx}`);
        }
      }

      if (this.needsRowId || this.field.key === 'exchange') {
        this.field.fieldGroup.forEach((group, idx) => {
          group.fieldGroup.forEach(x => {
            const fieldKey = x.key as string;

            if (fieldKey.includes('SourceBridgeID')) {
              if (!x.formControl.value) {
                x.formControl.setValue(`${this.rowIdPrefix}-${idx}`);
              }
            }

            if (fieldKey.includes('TSPSourceID')) {
              if (!x.formControl.value) {
                x.formControl.setValue(`TSPFundDetails-${idx}`);
              }
            }
          });
        });
      }
    }

    if (
      this.field.fieldArray['props'].disableCheck ||
      (this.field.fieldArray['props'].entryLimit &&
        this.maxEntries > 0 &&
        this.field.model.length >= this.maxEntries)
    ) {
      this.disableAdd = true;
      this.detector.detectChanges();
    } else {
      this.disableAdd = false;
      this.detector.detectChanges();
    }

    this.selectedIndex = this.field.fieldGroup.length - 1;

    if (navToTop) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }

    this.loading = false;
  }

  remove(index) {
    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content: 'Are you sure you would like to remove this record?',
        confirm: 'Remove',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        Object.keys(this.model[index]).forEach(x => {
          if (Array.isArray(this.model[index][x])) {
            this.model[index][x] = [];
          }
        });

        super.remove(index);

        if (
          this.field.fieldArray['props'].disableCheck ||
          (this.field.fieldArray['props'].entryLimit &&
            this.maxEntries > 0 &&
            this.field.model.length >= this.maxEntries)
        ) {
          this.disableAdd = true;
          this.detector.detectChanges();
        } else {
          this.disableAdd = false;
          this.detector.detectChanges();
        }

        this.selectedIndex = index - 1;
      }
    });
  }

  getField(field: FormlyFieldConfig, key, rowIndex: number): FormlyFieldConfig {
    return field.fieldGroup[rowIndex]
      ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === key)
      : null;
  }

  containsCurrency(value) {
    if (!isNaN(value)) {
      return false;
    } else {
      return value?.includes('$');
    }
  }

  showDelete(subField) {
    let show = false;

    if (
      ((this.field.key === 'exchange' && subField.isExchange) ||
        !this.field.fieldArray['props'].minEntries ||
        (this.field.fieldArray['props'].minEntries &&
          this.field.model.length > 1)) &&
      !this.hideAddRemove
    ) {
      show = true;
    }

    return show;
  }

  exitButton(ev, index) {
    ev.stopPropagation();
    this.remove(index);
  }

  getTooltip(subField: any, field: any, i: number): string | null {
    if (!subField?.model || !field?.fieldArray?.props) {
      return null;
    }

    const headerName = field.fieldArray.props.headerName;
    const headerAlt = field.fieldArray.props.headerAlt;
    const headerAmount = field.fieldArray.props.headerAmount;

    // Check for headerName or headerAlt
    const headerValue = subField.model[headerName] || subField.model[headerAlt];

    // Check for headerAmount
    if (headerAmount && subField.model[headerAmount]) {
      const amount = subField.model[headerAmount];

      if (!amount.includes('$')) {
        return headerValue
          ? `${headerValue} (${this.formatCurrency(amount)})`
          : `Source ${i + 1} ${this.formatCurrency(amount)}`;
      } else {
        return headerValue
          ? `${headerValue} (${amount})`
          : `Source ${i + 1} (${amount})`;
      }
    }

    // Default value
    return headerValue || `Source ${i + 1} ($0)`;
  }

  private formatCurrency(amount: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(parseFloat(amount));
  }
}
